import type { Nullable } from '@synqo/frontend-component-library';

const baseKeywords = ['x_offset', 'y_offset'] as const;
export type BaseKeyword = (typeof baseKeywords)[number];

/**
 * Maps alias keywords to their corresponding base keyword.
 * This is the single source of truth for all aliases.
 */
const aliasToBaseMap = {
    offset: 'y_offset',
} as const;

/**
 * A comprehensive map from any known keyword (base or alias) to its base keyword.
 */
const keywordToBaseMap = {
    ...Object.fromEntries(baseKeywords.map((k) => [k, k])),
    ...aliasToBaseMap,
} as const;

export const keywords = Object.keys(keywordToBaseMap) as (keyof typeof keywordToBaseMap)[];
export type Keyword = (typeof keywords)[number];

// Create a single regex for parsing.
// Keywords are sorted by length (desc) to ensure the longest match wins
// (e.g., 'x_offset' is checked before 'offset').
const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);
const splitNameRegex = new RegExp(`^(.*?)_(${sortedKeywords.join('|')})$`);

export type NameParts = {
    keyword: Nullable<BaseKeyword>;
    group: string;
};

export const splitName = (name: string): NameParts => {
    const match = name.match(splitNameRegex);

    if (!match) {
        return {
            group: name,
            keyword: null,
        };
    }

    const [, group, matchedKeyword] = match;

    return {
        group,
        keyword: keywordToBaseMap[matchedKeyword as Keyword],
    };
};
